import {
  Box,
  Chip,
  FormHelperText,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { FormikErrors } from "formik";

import AppIconButton from "@/common/components/Button/AppIconButton";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { FileItem } from "@/common/fileItem";
import { GenericDamage } from "@/common/ts/visualModel";
import { ValidationHelper } from "@/common/validation";
import { VehicleArea } from "@/core/api/generated/v1";

import VehicleDamageLink from "../../../../VehicleDamage/VehicleDamageLink";

interface Props<TDamage extends GenericDamage> {
  damages: TDamage[];
  isViewEnabled: boolean;
  isUpdateEnabled: boolean;
  isDeleteEnabled: boolean;
  withAlertsColumn?: boolean;
  errorsByDamageIdMap?: Record<string, FormikErrors<TDamage> | undefined>;
  onView: ((damage: TDamage) => void) | null | undefined;
  onUpdate: ((damage: TDamage) => void) | null | undefined;
  onDelete: ((damage: TDamage) => void) | null | undefined;
}

export function GenericDamageList<TDamage extends GenericDamage>({
  damages,
  isViewEnabled = false,
  isUpdateEnabled = false,
  isDeleteEnabled = false,
  withAlertsColumn = false,
  errorsByDamageIdMap,
  onView,
  onUpdate,
  onDelete,
}: Props<TDamage>) {
  return (
    <DataTabular
      columns={[
        {
          field: "partType",
          title: "Part type",
          flex: 1,
          renderCell: (item) =>
            item.partType?.name || item.partTypeRef?.name || item.area || item.projection ? (
              <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                <Box>{item.partType?.name || item.partTypeRef?.name || "-"}</Box>

                {(item.area || item.projection) && (
                  <Typography component='div' variant='body2' color='text.secondary'>
                    ({item.area ? <InlineApiEnumValue type='VehicleArea' value={item.area} /> : "-"}{" "}
                    /{" "}
                    {item.projection ? (
                      <InlineApiEnumValue type='VehicleProjection' value={item.projection} />
                    ) : (
                      "-"
                    )}
                    )
                  </Typography>
                )}
              </Stack>
            ) : (
              "-"
            ),
        },
        {
          field: "damageType",
          title: "Damage type",
          flex: 1,
          renderCell: (item) => <>{item.damageType?.name || item.damageTypeRef?.name || "-"}</>,
        },
        {
          field: "damage.id",
          title: "Damage",
          flex: 1,
          renderCell: (item) =>
            item.vehicleDamageId || item.vehicleDamage?.id || item.vehicleDamage ? (
              <VehicleDamageLink
                vehicleId={item.vehicleDamage?.vehicle?.id}
                entityId={item.vehicleDamageId || item.vehicleDamage?.id}
                entity={item.vehicleDamage}
                inlineProps={{ withIcon: true }}
              />
            ) : (
              "-"
            ),
        },
        {
          field: "notes",
          title: "Notes",
          width: 200,
          renderCell: (item) => (
            <AppTooltip title={item.notes || ""}>
              <AppTypography ellipsing={{ enabled: true }}>{item.notes}</AppTypography>
            </AppTooltip>
          ),
        },
        {
          field: "attachments",
          title: "Attachments",
          width: 150,
          renderCell: (item) => (
            <>
              {item.attachments && item.attachments?.length !== 0 && (
                <FileListView
                  files={FileItem.createManyFrom(item.attachments)}
                  maxVisibleFiles={2}
                  itemWidth={50}
                  itemHeight={30}
                  itemActions={() => ({
                    enabled: true,
                    click: true,
                    download: true,
                  })}
                  fileItemViewProps={{
                    sizeVariant: "compact",
                  }}
                />
              )}
            </>
          ),
        },
        {
          if: withAlertsColumn,
          field: "alerts",
          title: "Alerts",
          flex: 0,
          renderCell: (item) => {
            const damageErrors = errorsByDamageIdMap ? errorsByDamageIdMap[item.id] : undefined;
            const isAreaNotSet = !item.area;
            const isProjectionNoSet =
              item.area === VehicleArea.Exterior && !item.projection && !!item.point;
            const isPointNotSet = item.area === VehicleArea.Exterior && !item.point;

            return (
              <Box>
                {damageErrors && (
                  <AppPopover
                    hoverBehavior={{}}
                    trigger={
                      <Chip
                        variant='outlined'
                        size='small'
                        color='error'
                        label={<AppIcon of='error' inText />}
                      />
                    }
                  >
                    <AppPopoverContent sx={{ maxWidth: 300 }}>
                      <FormHelperText error>
                        <Box>Invalid damage data.</Box>
                        <Box>
                          {ValidationHelper.getFormikErrorsAsString(damageErrors, {
                            isIncludeNested: false,
                          })}
                        </Box>
                      </FormHelperText>
                    </AppPopoverContent>
                  </AppPopover>
                )}

                {(isAreaNotSet || isProjectionNoSet || isPointNotSet) && (
                  <AppPopover
                    hoverBehavior={{}}
                    trigger={
                      <Chip
                        variant='outlined'
                        size='small'
                        color='warning'
                        label={<AppIcon of='warning' inText />}
                      />
                    }
                  >
                    <AppPopoverContent sx={{ maxWidth: 300 }}>
                      {isAreaNotSet && <Box>{`Area isn't specified. It's required.`}</Box>}

                      {isProjectionNoSet && (
                        <Box>{`Projection isn't specified. It's required for exterior damages to correctly display damage point.`}</Box>
                      )}

                      {isPointNotSet && (
                        <Box>
                          {`Damage point isn't specified. It's required for exterior damages.`}
                        </Box>
                      )}
                    </AppPopoverContent>
                  </AppPopover>
                )}
              </Box>
            );
          },
        },
      ]}
      rows={damages}
      getRowId={(item) => item.id!}
      borderVariant='bordered'
      rowHeight={65}
      isColumnSelectorEnabled={false}
      isColumnMenuEnabled={false}
      isPaginationEnabled={false}
      renderRowAction={({ item }) =>
        isViewEnabled || isUpdateEnabled || isDeleteEnabled ? (
          <MenuWithTrigger
            withAuthCloseOnClick
            trigger={
              <AppIconButton>
                <AppIcon of='moreVert' />
              </AppIconButton>
            }
          >
            {isViewEnabled && (
              <MenuItem
                onClick={() => {
                  onView && onView(item);
                }}
              >
                <ListItemIcon>
                  <AppIcon of='view' fontSize='small' />
                </ListItemIcon>
                <ListItemText>View</ListItemText>
              </MenuItem>
            )}

            {isUpdateEnabled && (
              <MenuItem
                onClick={() => {
                  onUpdate && onUpdate(item);
                }}
              >
                <ListItemIcon>
                  <AppIcon of='edit' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
            )}

            {isDeleteEnabled && (
              <MenuItem
                onClick={() => {
                  onDelete && onDelete(item);
                }}
              >
                <ListItemIcon>
                  <AppIcon of='delete' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            )}
          </MenuWithTrigger>
        ) : undefined
      }
    />
  );
}
